/**
 * Main dashboard page with dark theme styling.
 */

import { useState, useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { MainLayout } from '@/components/layout';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner } from '@/components/ui';
import {
  AccountSelector,
  AccountOverview,
  PositionList,
  KPICards,
  RiskMetrics,
  WashSalesWidget,
} from '@/components/dashboard';
import { PerformanceChart, AllocationChart } from '@/components/charts';
import {
  useAccounts,
  useAccountSummary,
  useAccountSettings,
  useAllPositions,
  usePerformance,
  calculateAllocations,
  TIME_RANGE_OPTIONS,
  type TimeRange,
} from '@/hooks';
import { formatDateTime } from '@/utils/formatters';

export function DashboardPage() {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const { mutate } = useSWRConfig();

  const { accounts, isLoading: accountsLoading } = useAccounts();
  const { summary, isLoading: summaryLoading } = useAccountSummary(selectedAccountId);
  const {
    settings,
    isSaving: nlvSaving,
    saveError: nlvSaveError,
    saveNlv,
  } = useAccountSettings(selectedAccountId);
  const {
    positions,
    isLoading: positionsLoading,
    isRefreshing,
    refresh: refreshPositions,
  } = useAllPositions(selectedAccountId, { status: 'ACTIVE' });
  const { data: performanceData, isLoading: performanceLoading } = usePerformance(
    selectedAccountId,
    { timeRange }
  );

  const allocations = calculateAllocations(positions, settings.nlv).map((a) => ({
    symbol: a.symbol,
    value: a.value,
    percentage: a.percent,
  }));

  const handleAccountSelect = useCallback((accountId: string) => {
    setSelectedAccountId(accountId);
  }, []);

  const handleRefresh = useCallback(() => {
    refreshPositions();
    if (selectedAccountId) {
      mutate(['wash-sales', selectedAccountId]);
    }
  }, [refreshPositions, mutate, selectedAccountId]);

  if (!selectedAccountId && accounts.length > 0 && !accountsLoading) {
    const firstAccount = accounts[0];
    if (firstAccount) {
      setSelectedAccountId(firstAccount.account_id);
    }
  }

  const isLoading = accountsLoading || (selectedAccountId && positionsLoading);
  const lastUpdated = summary?.last_updated ?? new Date().toISOString();

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-dark-100">Dashboard</h1>
            <AccountSelector
              accounts={accounts}
              selectedAccountId={selectedAccountId}
              onSelect={handleAccountSelect}
              isLoading={accountsLoading}
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <div className="absolute inset-0 h-2 w-2 animate-ping rounded-full bg-emerald-400 opacity-75" />
              </div>
              <span className="text-sm text-dark-400">
                {formatDateTime(lastUpdated)}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={!selectedAccountId || isRefreshing}
              glow
            >
              {isRefreshing ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <Spinner size="lg" />
              <p className="text-dark-400 text-sm">Loading portfolio data...</p>
            </div>
          </div>
        )}

        {/* No account selected */}
        {!isLoading && !selectedAccountId && (
          <Card className="border-dashed border-dark-600">
            <CardContent className="py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-dark-800 flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <p className="text-dark-400">Select an account to view portfolio details</p>
            </CardContent>
          </Card>
        )}

        {/* Dashboard content */}
        {!isLoading && selectedAccountId && (
          <>
            <AccountOverview
              summary={summary}
              isLoading={summaryLoading}
              nlv={settings.nlv}
              nlvUpdatedAt={settings.nlv_updated_at}
              nlvSource={settings.nlv_source ?? null}
              nlvSaving={nlvSaving}
              nlvSaveError={nlvSaveError}
              onNlvSave={saveNlv}
            />
            <KPICards kpi={summary?.kpi} isLoading={summaryLoading} />
            <WashSalesWidget accountId={selectedAccountId} />

            {/* Charts Row */}
            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Performance</CardTitle>
                  <div className="flex gap-1 rounded-lg bg-dark-800 p-1">
                    {TIME_RANGE_OPTIONS.slice(0, 4).map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setTimeRange(option.value)}
                        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                          timeRange === option.value
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'text-dark-400 hover:text-dark-200 hover:bg-dark-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  {performanceLoading ? (
                    <div className="flex h-64 items-center justify-center">
                      <Spinner size="lg" />
                    </div>
                  ) : (
                    <PerformanceChart
                      data={performanceData.map((d) => ({ date: d.date, value: d.value }))}
                    />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Allocation</CardTitle>
                </CardHeader>
                <CardContent>
                  {positionsLoading ? (
                    <div className="flex h-64 items-center justify-center">
                      <Spinner size="lg" />
                    </div>
                  ) : allocations.length === 0 ? (
                    <div className="flex h-64 items-center justify-center">
                      <p className="text-dark-500">No positions to display</p>
                    </div>
                  ) : (
                    <AllocationChart data={allocations} />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Risk Metrics and Position List */}
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <RiskMetrics positions={positions} isLoading={positionsLoading} nlv={settings.nlv} />
              </div>
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Open Positions ({positions.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="p-4">
                      <PositionList
                        positions={positions}
                        isLoading={positionsLoading}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
